import { useCallback, useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../../utils/networking";

/** `recent` = the panel on top of the history feed, `full` = the dialog. */
export type CaseSummaryKind = "recent" | "full";

export interface CaseSummaryPayload {
  kind?: CaseSummaryKind;
  summary: string | null;
  generated_at: string | null;
  activity_count?: number;
  source?: "cache" | "generated" | "none";
  error?: "not_configured" | "unavailable";
}

const UNAVAILABLE: CaseSummaryPayload = {
  summary: null,
  generated_at: null,
  error: "unavailable",
};

/**
 * Fetches one kind of generated case summary and exposes a regenerate action.
 *
 * Refetches whenever `activityCount` moves (the server regenerates when its own
 * activity count has moved on). With `enabled: false` nothing is fetched, so
 * the full summary is only generated once its dialog is opened.
 */
export const useCaseSummary = (
  caseId: string,
  kind: CaseSummaryKind,
  activityCount: number,
  enabled: boolean = true,
) => {
  const [data, setData] = useState<CaseSummaryPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [regenerating, setRegenerating] = useState<boolean>(false);
  const alive = useRef<boolean>(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!caseId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axiosInstance
      .get(`/cases/summary?case_id=${caseId}&kind=${kind}`)
      // `data` and `loading` are set together so React 18 batches them into a
      // single commit (two commits make the panel flicker, and make every test
      // that waits on it race the second update).
      .then((res: any) => {
        if (!alive.current) return;
        setData((res && res.data) || UNAVAILABLE);
        setLoading(false);
      })
      .catch(() => {
        if (!alive.current) return;
        setData(UNAVAILABLE);
        setLoading(false);
      });
  }, [caseId, kind]);

  useEffect(() => {
    if (enabled) load();
    // activityCount is part of the cache key on the server side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, kind, activityCount, enabled]);

  const regenerate = () => {
    if (regenerating) return;
    setRegenerating(true);
    axiosInstance
      .post(`/cases/summary/regenerate`, { case_id: caseId, kind })
      .then((res: any) => {
        if (!alive.current) return;
        if (res && res.data) setData(res.data);
      })
      .catch(() => {
        if (!alive.current) return;
        setData((current) => (current && current.summary ? current : UNAVAILABLE));
      })
      .finally(() => {
        if (alive.current) setRegenerating(false);
      });
  };

  return { data, loading, regenerating, load, regenerate };
};
